import { Platform } from 'react-native';
import { logDebug, logError, logWarning } from './logger';
import Amplitude from './amplitude';
import Datadog from './datadog';
import { DATADOG_CUSTOM_ACTIONS } from './datadog/DatadogCustomActions';
import { AnalyticsEvent } from './analyticsEvents';

const FLUSH_DEFAULT_TIMEOUT_MS = 800;

/**
 * Cap on the SDK-discovery phase alone. Without it a tag that never fires — no
 * consent, blocked, misconfigured — costs the caller the whole budget on every
 * add to cart.
 */
const FLUSH_SDK_DISCOVERY_MS = 400;

const FLUSH_SDK_POLL_MS = 25;

/** Time given to the tag to call `logCustomEvent` once its SDK is there. */
export const FLUSH_LOG_GRACE_MS = 100;

export const wait = (ms: number): Promise<void> => new Promise<void>(resolve => setTimeout(resolve, ms));

type FlushOutcome = 'sent' | 'not-sent' | 'timeout' | 'no-push' | 'no-sdk' | 'no-consent';

type FlushPath = 'gtm' | 'sdk';

export type FlushFallbackReason = 'flag-off' | 'sdk-not-ready';

export type BrazeEvent = {
  event: AnalyticsEvent;
  properties?: Record<string, unknown>;
};

type Flush = (callback?: (success: boolean) => void) => void;

const flushOutcomeFor = (success: boolean | null): FlushOutcome => {
  if (success === null) return 'timeout';

  return success ? 'sent' : 'not-sent';
};

/** Resolves with what the SDK reports, or `null` if it never calls back. */
const awaitFlush = (flush: Flush): Promise<boolean | null> =>
  new Promise(resolve => {
    try {
      flush(success => resolve(success));
    } catch {
      // An SDK blocked by an ad blocker must not hold up the redirect.
      resolve(null);
    }
  });

/** Pushes in isolation: one failing push must neither stop the others nor the redirect. */
const tryPush = (push: () => void): boolean => {
  try {
    push();
    return true;
  } catch {
    return false;
  }
};

class Braze {
  public isEnabled: boolean = false;
  private _brazeService: any | undefined;
  private _userId: string | null = null;

  public async init(): Promise<void> {
    this.isEnabled = true;
    if (!this.isInitialized) {
      try {
        this._brazeService ??= (await import('@photosi/braze-service')).brazeService;
        this._brazeService.initializeSDK();
        if (this._userId) {
          this._brazeService.setUserId(this._userId);
        }
      } catch (error) {
        logWarning('[BRAZE] - client initialization threw an error', error);
      }
    }
  }

  public setUserInfo(userId: string | null, userType: string): void {
    if (!userId) {
      return;
    }

    this._userId = userId;
    this._brazeService?.setUserId(userId);
  }

  public async isSdkReady(): Promise<boolean> {
    if (!this.isEnabled) {
      return false;
    }

    const discoveryDeadline = performance.now() + FLUSH_SDK_DISCOVERY_MS;
    while (!this._isSdkReady() && performance.now() + FLUSH_SDK_POLL_MS < discoveryDeadline) {
      await wait(FLUSH_SDK_POLL_MS);
    }

    return this._isSdkReady();
  }

  public async trackEventsAndFlush(events: BrazeEvent[], timeoutMs: number = FLUSH_DEFAULT_TIMEOUT_MS): Promise<void> {
    const startedAt = performance.now();

    const sentEvents = events.filter(({ event, properties }) => {
      try {
        this._emitEvent(event.name, properties);
        return true;
      } catch (error) {
        logError('[BRAZE] - failed to track event', error);
        return false;
      }
    });

    if (Platform.OS !== 'web') {
      return;
    }

    if (sentEvents.length === 0) {
      this._reportFlush('no-push', performance.now() - startedAt, null, 'sdk');
      return;
    }

    const success = await Promise.race([awaitFlush(this.requestImmediateDataFlush.bind(this)), wait(timeoutMs)]).then(
      result => (typeof result === 'boolean' ? result : null),
    );

    this._reportFlush(flushOutcomeFor(success), performance.now() - startedAt, null, 'sdk');
  }

  public getBrazeFeatureFlag(feature: string) {
    let featureFlag;
    if (this.isInitialized) {
      featureFlag = this._brazeService?.getFeatureFlag(feature);
      const featureEnabled: boolean = !!featureFlag?.enabled;
      try {
        Amplitude.setAmplitudeFeatureFlag(feature, `${featureEnabled}`);
        Datadog.setDatadogFeatureFlag(feature, `${featureEnabled}`);
      } catch (err) {
        logError('[BRAZE] [getBrazeFeatureFlag] ', err);
      }
    }
    return featureFlag;
  }

  public trackEvent(event: AnalyticsEvent, eventProperties?: {}) {
    this._emitEvent(event.name, { platform: 'web', ...eventProperties });
  }

  private _emitEvent(name: string, eventProperties: Record<string, unknown> = {}): void {
    if (__DEV__) {
      logDebug(`[BRAZE] - MOCKED send event: ${name} | Data: `, eventProperties, ` | Time: `, new Date().toISOString());
    } else {
      this._brazeService?.emitEvent({ eventName: name, eventProperties });
    }
  }

  public requestImmediateDataFlush(callback?: (success: boolean) => void) {
    try {
      if (this.isInitialized) {
        this._sdk?.requestImmediateDataFlush(callback);
      }
    } catch (error) {
      logError('[BRAZE] - failed to request immediate data flush', error);
    }
  }

  /**
   * Pushes one or more events (typically to GTM) and holds the caller until Braze reports
   * that it has sent what was queued (TGTM-300). The SDK batches on a 10s interval over a
   * plain XHR — no `sendBeacon`, no `keepalive` — so without an immediate flush the
   * navigation that follows this call always wins and the events are lost.
   *
   * Several pushes are spaced by the log grace, so the tag reads each one's dataLayer state
   * before the next overwrites it, and then share a single flush: one request instead of
   * one per event.
   *
   * Never rejects, and never holds the caller longer than `timeoutMs` (by default scaled
   * with the number of pushes) once the pushes are out. A push that throws is skipped, not
   * fatal. Native has no GTM container and no Braze web SDK, so there is no queue to flush
   * and no document being replaced: the pushes are forwarded and the caller proceeds.
   */
  public async flushAfter(
    pushes: (() => void) | (() => void)[],
    timeoutMs?: number,
    fallbackReason?: FlushFallbackReason,
  ): Promise<void> {
    const [push, ...nextPushes] = Array.isArray(pushes) ? pushes : [pushes];
    if (!push) return;

    if (Platform.OS !== 'web') {
      [push, ...nextPushes].forEach(tryPush);
      return;
    }

    const startedAt = performance.now();
    const deadline = startedAt + (timeoutMs ?? FLUSH_DEFAULT_TIMEOUT_MS + nextPushes.length * FLUSH_LOG_GRACE_MS);

    let pushed = tryPush(push);

    // The tag loads its SDK on demand, at click time, precisely because it is about
    // to log — so its appearance is the signal to wait for, not a blanket delay. Pointless
    // when Braze will never receive anything, or when nothing was pushed to log.
    let flush = this._getFlush();
    if (this.isEnabled && pushed) {
      const discoveryDeadline = Math.min(startedAt + FLUSH_SDK_DISCOVERY_MS, deadline);
      while (!flush && performance.now() + FLUSH_SDK_POLL_MS < discoveryDeadline) {
        await wait(FLUSH_SDK_POLL_MS);
        flush = this._getFlush();
      }
    }
    let sdkFoundMs = flush ? Math.round(performance.now() - startedAt) : null;

    // The pushes still go to GTM for the other tags on it, and stay spaced even when no SDK
    // showed up yet: a tag whose SDK loads late still reads each one's dataLayer state, and
    // the grace keeps the next push from overwriting what the tag is about to read.
    for (const nextPush of nextPushes) {
      await wait(FLUSH_LOG_GRACE_MS);
      pushed = tryPush(nextPush) || pushed;
    }

    if (!pushed) {
      // Nothing reached an SDK, so waiting would only delay the navigation.
      this._reportFlush('no-push', performance.now() - startedAt, null, 'gtm', fallbackReason);
      return;
    }

    if (!this.isEnabled) {
      this._reportFlush('no-consent', performance.now() - startedAt, null, 'gtm', fallbackReason);
      return;
    }

    // An SDK that loaded while the later pushes were spaced out still gets them sent.
    if (!flush) {
      flush = this._getFlush();
      sdkFoundMs = flush ? Math.round(performance.now() - startedAt) : null;
    }

    if (!flush) {
      this._reportFlush('no-sdk', performance.now() - startedAt, null, 'gtm', fallbackReason);
      return;
    }

    // `logCustomEvent` follows the SDK load, so the queue is not filled yet. Flushing
    // too early empties nothing and gets an honest-looking `success: true` back.
    await wait(FLUSH_LOG_GRACE_MS);

    const success = await Promise.race([awaitFlush(flush), wait(Math.max(0, deadline - performance.now()))]).then(
      result => (typeof result === 'boolean' ? result : null),
    );

    this._reportFlush(flushOutcomeFor(success), performance.now() - startedAt, sdkFoundMs, 'gtm', fallbackReason);
  }

  private _getFlush(): Flush | null {
    return this.isInitialized ? this.requestImmediateDataFlush.bind(this) : null;
  }

  /** `sdkFoundMs` near zero means the tag's SDK was already there, so its presence said nothing about this event. */
  private _reportFlush(
    outcome: FlushOutcome,
    durationMs: number,
    sdkFoundMs: number | null,
    path: FlushPath,
    fallbackReason?: FlushFallbackReason,
  ): void {
    Datadog.recordMeasure(DATADOG_CUSTOM_ACTIONS.web_braze_flush_before_redirect, Math.round(durationMs), {
      outcome,
      sdkFoundMs,
      path,
      fallbackReason,
    });
  }

  public get isInitialized(): boolean {
    return (this._sdk && this._sdk.isInitialized()) ?? false;
  }

  private _isSdkReady(): boolean {
    try {
      return this.isEnabled && this.isInitialized;
    } catch {
      return false;
    }
  }

  private get _sdk() {
    return this._brazeService?.brazeInstance();
  }
}

export default new Braze();
